import type {
  Notification,
  NotificationItineraryView,
} from '@notifications/entities/notification';
import {
  formatNotificationTime,
  formatNotificationTimeLong,
  presentNotification,
} from '@notifications/entities/notification';
import { CheckCheck, ChevronDown } from 'lucide-react';
import { memo, useId } from 'react';
import { joinCss } from '@/shared/lib/css-utils';
import { Button, Text } from '@/shared/ui';
import css from './notification-row.module.scss';

type NotificationRowProps = {
  notification: Notification;
  expanded: boolean;
  onToggle: (id: string) => void;
  onOpenItinerary: (
    itineraryId: string,
    view: NotificationItineraryView
  ) => void;
  onOpenSupportTicket: (ticketId: string) => void;
};

// One list row. Collapsed it shows only what makes a list scannable — the event
// and when it happened; everything else lives behind the expansion.
//
// `memo` matters here: the provider re-renders the whole list on every poll, and
// without it expanding one row would re-render every row. The props are a stable
// notification object (structural sharing keeps identity across an unchanged
// refetch) plus two stable callbacks, so a poll that changes nothing re-renders
// nothing.
const NotificationRowComponent = ({
  notification,
  expanded,
  onToggle,
  onOpenItinerary,
  onOpenSupportTicket,
}: NotificationRowProps) => {
  const panelId = useId();
  const unread = notification.readOn === null;
  const {
    headline,
    detail,
    subject,
    itineraryId,
    itineraryView,
    supportTicketId,
  } = presentNotification(notification);
  // At most one of the two is ever set (the entity type decides), so a single
  // action renders whichever the notification actually points at.
  const openTarget = itineraryId
    ? () => onOpenItinerary(itineraryId, itineraryView)
    : supportTicketId
      ? () => onOpenSupportTicket(supportTicketId)
      : null;
  const when = formatNotificationTime(notification.createdOn);
  const whenLong = formatNotificationTimeLong(notification.createdOn);

  return (
    <li className={css.root}>
      {/*
        Unread is the ONLY state the card itself carries — a subtle grey fill
        behind the dot. Hover and expansion change nothing about it; expansion
        lives entirely in the panel's height plus the chevron (which the
        stylesheet rotates off `aria-expanded`).
      */}
      <div className={joinCss(css.card, unread && css.cardUnread)}>
        <button
          aria-controls={panelId}
          aria-expanded={expanded}
          // Spoken as one sentence: what happened, when, and whether it is new.
          aria-label={`${headline}, ${whenLong}, ${unread ? 'unread' : 'read'}`}
          className={css.row}
          onClick={() => onToggle(notification.id)}
          type="button"
        >
          <span aria-hidden="true" className={css.indicator}>
            {unread ? (
              <span className={css.dot} />
            ) : (
              <CheckCheck className={css.readIcon} />
            )}
          </span>

          {/* One weight for every card — the dot carries the unread state. */}
          <span className={css.headline}>
            <Text as="span">{headline}</Text>
          </span>

          <time className={css.time} dateTime={notification.createdOn}>
            {when}
          </time>

          <ChevronDown aria-hidden="true" className={css.chevron} />
        </button>

        {/*
          Always rendered so the expand/collapse can animate between grid rows
          of 0fr and 1fr — a height transition with no JS measuring. `inert`
          keeps the collapsed content out of the tab order and off screen
          readers.
        */}
        <div
          className={joinCss(css.panel, expanded && css.panelOpen)}
          id={panelId}
          inert={!expanded}
        >
          <div className={css.panelInner}>
            <div className={css.panelContent}>
              {subject ? (
                <Text className={css.subject} weight="medium">
                  {subject}
                </Text>
              ) : null}
              {/* The event's own sentence — e.g. who commented on the ticket. */}
              <Text size="sm" variant="muted">
                {detail}
              </Text>
              <Text as="span" size="xs" variant="muted">
                {whenLong}
              </Text>
              {openTarget ? (
                <div className={css.actions}>
                  <Button onClick={openTarget} size="sm" variant="outline">
                    View details
                  </Button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </li>
  );
};

export const NotificationRow = memo(NotificationRowComponent);
